import { getMediaLibraryApi } from '@/core/api/generatedApiClient';
import { INLINE_API_ERROR_OPTIONS } from '@/core/api/requestErrorMode';
import { rethrowHandledApiError } from '@/core/api/rethrowHandledApiError';
import type {
    ImportMediaRequest,
    MediaAssetPageResponse,
    MediaFolderResponse,
    MediaAssetResponse,
    MediaAssetDetailsResponse,
    MediaStoragePageResponse,
    MediaBulkUpdateRequest,
    MediaLibraryApiGetMediaAssetsRequest,
    MediaVersionRequest,
    SaveMediaFolderRequest,
    UpdateMediaAssetRequest,
} from '@/core/generated/api';

/** Выполняет запрос медиатеки с общей обработкой ошибок */
const request = async <T>(action: () => Promise<{ data: T }>): Promise<T> => {
    try {
        return (await action()).data;
    } catch (error: unknown) {
        return rethrowHandledApiError(error, 'inline');
    }
};

/** Управляет файлами медиатеки через административный API */
export const mediaLibraryService = {
    /** Получает страницу файлов */
    list: (query: MediaLibraryApiGetMediaAssetsRequest): Promise<MediaAssetPageResponse> => request(() => getMediaLibraryApi().getMediaAssets(query, INLINE_API_ERROR_OPTIONS)),

    /** Получает дерево папок */
    folders: (): Promise<MediaFolderResponse[]> => request(() => getMediaLibraryApi().getMediaFolders({}, INLINE_API_ERROR_OPTIONS)),

    /** Получает сведения, использование и историю файла */
    details: (id: string): Promise<MediaAssetDetailsResponse> => request(() => getMediaLibraryApi().getMediaAssetDetails({ id }, INLINE_API_ERROR_OPTIONS)),

    /** Создаёт или изменяет папку */
    saveFolder: (body: SaveMediaFolderRequest, id?: string): Promise<string | void> => id
        ? request(() => getMediaLibraryApi()
            .updateMediaFolder({
                id,
                saveMediaFolderRequest: body
            }, INLINE_API_ERROR_OPTIONS))
        : request(() => getMediaLibraryApi().createMediaFolder({ saveMediaFolderRequest: body }, INLINE_API_ERROR_OPTIONS)),

    /** Удаляет пустую папку */
    deleteFolder: (id: string, version: string): Promise<void> => request(() => getMediaLibraryApi()
        .deleteMediaFolder({
            id,
            version
        }, INLINE_API_ERROR_OPTIONS)),

    /** Сохраняет название, папку и теги файла */
    update: (id: string, body: UpdateMediaAssetRequest): Promise<void> => request(() => getMediaLibraryApi()
        .updateMediaAsset({
            id,
            updateMediaAssetRequest: body
        }, INLINE_API_ERROR_OPTIONS)),

    /** Изменяет выбранные файлы */
    bulk: (body: MediaBulkUpdateRequest): Promise<void> => request(() => getMediaLibraryApi().updateMediaSelection({ mediaBulkUpdateRequest: body }, INLINE_API_ERROR_OPTIONS)),

    /** Удаляет неиспользуемые файлы из каталога */
    remove: (items: MediaVersionRequest[]): Promise<void> => request(() => getMediaLibraryApi().deleteMediaAssets({ mediaVersionRequest: items }, INLINE_API_ERROR_OPTIONS)),

    /** Загружает изображение с прогрессом передачи */
    upload: (
        file: File,
        folderId: string | undefined,
        progress: (percent: number) => void
    ): Promise<MediaAssetResponse> => request(() => getMediaLibraryApi()
        .uploadMediaAsset({
            file,
            folderId
        }, {
            ...INLINE_API_ERROR_OPTIONS,
            timeout: 120_000,
            /** Передаёт прогресс загрузки изображения */
            onUploadProgress: event => progress(event.total
                ? Math.round(event.loaded / event.total * 100)
                : 0),
        })),

    /** Проверяет чтение хранилища и получает страницу объектов */
    storage: (prefix: string, continuationToken?: string): Promise<MediaStoragePageResponse> => request(() => getMediaLibraryApi()
        .getMediaStorage({
            prefix,
            continuationToken
        }, INLINE_API_ERROR_OPTIONS)),

    /** Индексирует выбранные объекты S3 */
    import: (body: ImportMediaRequest): Promise<number> => request(() => getMediaLibraryApi()
        .importMediaAssets({ importMediaRequest: body }, {
            ...INLINE_API_ERROR_OPTIONS,
            timeout: 180_000
        })),

    /** Получает архив выбранных изображений */
    download: (ids: string[]): Promise<Blob> => request(() => getMediaLibraryApi()
        .downloadMediaAssets({ mediaSelectionRequest: { ids } }, {
            ...INLINE_API_ERROR_OPTIONS,
            responseType: 'blob',
            timeout: 180_000
        })),
};
